import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from '../../hooks/useLocation';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import './HeaderLocationSelector.css';

interface HeaderLocationSelectorProps {
  onLocationSelect: (location: string) => void;
  currentLocation?: string;
}

export const HeaderLocationSelector: React.FC<HeaderLocationSelectorProps> = ({ 
  onLocationSelect,
  currentLocation = ''
}) => {
  const [inputValue, setInputValue] = useState(currentLocation);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const { filterLocations } = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update input value when currentLocation changes
  useEffect(() => {
    setInputValue(currentLocation);
  }, [currentLocation]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (value.trim()) {
      const filtered = filterLocations(value);
      setFilteredLocations(filtered);
      setShowDropdown(true);
    } else {
      // Show all available locations when input is empty
      const allLocations = supabaseLocationService.getAvailableLocations().map(loc => loc.name);
      setFilteredLocations(allLocations);
      setShowDropdown(true);
    }
  };

  // Handle input focus - clear current location and move cursor to start
  const handleFocus = () => {
    // Clear the input to show only placeholder
    setInputValue('');
    // Move cursor to the beginning of the input
    if (inputRef.current) {
      inputRef.current.setSelectionRange(0, 0);
    }
    // Show dropdown with all available locations
    const allLocations = supabaseLocationService.getAvailableLocations().map(loc => loc.name);
    setFilteredLocations(allLocations);
    setShowDropdown(true);
  };

  // Handle location selection
  const handleLocationSelect = (location: string) => {
    setInputValue(location);
    setShowDropdown(false);
    onLocationSelect(location);
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filteredLocations.length > 0) {
      handleLocationSelect(filteredLocations[0]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setInputValue(currentLocation);
    }
  };



  // Handle input blur - restore current location if no selection made
  const handleBlur = () => {
    // Small delay to allow click events on dropdown items
    setTimeout(() => {
      if (!showDropdown) {
        setInputValue(currentLocation);
      }
    }, 150);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="header-location-selector">
      <div className="input-container">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Type to search locations..."
          className="header-location-input"
        />
        {showDropdown && filteredLocations.length > 0 && (
          <div ref={dropdownRef} className="header-autocomplete-dropdown">
            {filteredLocations.map((location, index) => {
              const locationInfo = supabaseLocationService.getAvailableLocations().find(loc => loc.name === location);
              return (
                <div
                  key={index}
                  className="header-autocomplete-item"
                  onClick={() => handleLocationSelect(location)}
                >
                  <div className="location-name">{location}</div>
                  {locationInfo && (
                    <div className="location-details">
                      <span className="location-type">{locationInfo.type}</span>
                      {locationInfo.type === 'city' && (
                        <span className="location-county">{locationInfo.county} County</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
